import {
  Badge,
  Box,
  SideNavigation,
  SideNavigationProps,
} from "@amzn/awsui-components-console";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useCart } from "@/contexts/CartContext";
import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { useUser } from "@/contexts/UserContext";
import { APP_VERSION, EXTERNAL_LINKS } from "@/utils/constants";

import { createChatNavItem } from "./utils";

/**
 * Orders navigation links by their displayed text, case-insensitively:
 * `localeCompare` weighs letters ahead of case, so a lowercase label sorts by
 * its letters rather than after every capitalised one the way a codepoint
 * comparison would.
 *
 * Named and exported so the ordering rule can be exercised over link sets the
 * sidebar does not ship today. The nine links below are all Title Case, which
 * leaves the case-insensitive half of the rule unobservable from them, and a
 * link added later cannot be observed from them at all.
 */
export const compareNavLinkText = (
  a: { text: string },
  b: { text: string }
): number => a.text.localeCompare(b.text);

/**
 * Admin pages, rendered alphabetically by their link text.
 *
 * The entries stay in the order they were written and the ordering is applied
 * by the `sort` below, so a link added later places itself by its text with no
 * change to the ordering logic. At module scope the sort runs once at import
 * rather than on every navigation recomputation.
 */
const ADMIN_NAV_LINKS: ReadonlyArray<{ text: string; href: string }> = [
  {
    text: "Cached Messages",
    href: "/admin/cached-messages",
  },
  {
    text: "Chat History",
    href: "/admin/chat-history",
  },
  {
    text: "Metadata Configuration",
    href: "/admin/metadata-configuration",
  },
  {
    text: "Agent Configuration",
    href: "/admin/agent-configuration",
  },
  {
    text: "Agent Evaluation",
    href: "/admin/evaluation",
  },
  {
    text: "Agent Evaluation Dataset",
    href: "/admin/eval-dataset",
  },
  {
    text: "Export/Transfer Locations",
    href: "/admin/export-locations",
  },
  {
    text: "User Analytics",
    href: "/admin/user-analytics",
  },
  {
    text: "User Share Access",
    href: "/admin/users",
  },
].sort(compareNavLinkText);

const Sidebar: React.FC = () => {
  const {
    user: { isAdmin },
  } = useUser();

  const navigate = useNavigate();
  const location = useLocation();

  const { items: chats, loading, loadChatHistory } = useChatHistory();
  const { items: cartItems } = useCart();

  // Initial load.
  useEffect(() => {
    loadChatHistory();
  }, [loadChatHistory]);

  const navItems = useMemo<ReadonlyArray<SideNavigationProps.Item>>(() => {
    const pinnedChats = chats.filter(({ isPinned }) => isPinned);
    const unpinnedChats = chats.filter(({ isPinned }) => !isPinned);

    return [
      ...(isAdmin
        ? [
            {
              type: "section-group",
              title: "Admin Features",
              items: ADMIN_NAV_LINKS.map(({ text, href }) => ({
                type: "link",
                text,
                href,
              })),
            },

            {
              type: "divider",
            },
          ]
        : []),
      ...(loading
        ? [
            {
              type: "section-group",
              title: "Loading...",
              items: [
                {
                  type: "link",
                  title: "", // Dummy item.
                },
              ],
            },
          ]
        : chats.length > 0
        ? [
            ...(pinnedChats.length > 0
              ? [
                  {
                    type: "section",
                    text: "Pinned Chats",
                    items: pinnedChats.map(createChatNavItem),
                  },
                ]
              : []),
            ...(unpinnedChats.length > 0
              ? [
                  {
                    type: "section",
                    text: "History (Past 7 Days)",
                    items: unpinnedChats.map(createChatNavItem),
                  },
                ]
              : []),
          ]
        : [
            {
              type: "link",
              text: "New Chat",
              href: "/",
            },
          ]),
      {
        type: "divider",
      },
      {
        type: "link",
        text: "Cart",
        href: "/cart",
        info:
          cartItems.length > 0 ? (
            <Badge color="red">{cartItems.length}</Badge>
          ) : undefined,
      },
      {
        type: "divider",
      },
      ...EXTERNAL_LINKS.map(({ title, url }) => ({
        type: "link",
        text: title,
        href: url,
        external: true,
      })),
    ] as SideNavigationProps.Item[];
  }, [chats, isAdmin, loading, cartItems]);

  return (
    <>
      <SideNavigation
        activeHref={location.pathname + location.search}
        header={{
          href: "/",
          text: "NBA Semantic Search",
        }}
        items={navItems}
        onFollow={(event) => {
          if (!event.detail.external) {
            event.preventDefault();
            navigate(event.detail.href);
          }
        }}
      />
      {/* Build version, shown at the foot of the expanded nav so users and
          support can identify which release they're on. */}
      <div data-testid="app-version">
        <Box
          padding={{ horizontal: "l", vertical: "xs" }}
          color="text-status-inactive"
          fontSize="body-s"
        >
          v{APP_VERSION}
        </Box>
      </div>
    </>
  );
};

export default Sidebar;
